import React, { useState } from "react";
import { Modal, Button, Form, Alert, Spinner } from "react-bootstrap";
import {
  FaCertificate,
  FaCalendarAlt,
  FaIdCard,
  FaUpload,
  FaTimes,
} from "react-icons/fa";
import { toast } from "react-toastify";
import CertificateService from "../../services/certificateService";

const IssueCertificateModal = ({
  show,
  onHide,
  student,
  batchId,
  batchName,
  onSuccess,
}) => {
  const [formData, setFormData] = useState({
    certificate_number: "",
    issue_date: new Date().toISOString().split("T")[0],
  });
  const [certificateImage, setCertificateImage] = useState(null);
  const [imagePreview, setImagePreview] = useState(null);
  const [loading, setLoading] = useState(false);
  const [errors, setErrors] = useState({});

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
    if (errors[name]) {
      setErrors((prev) => ({ ...prev, [name]: null }));
    }
  };

  const handleImageChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      // Validate file type
      const allowedTypes = [
        "image/jpeg",
        "image/png",
        "image/jpg",
        "application/pdf",
      ];
      if (!allowedTypes.includes(file.type)) {
        toast.error("Only JPG, PNG, or PDF files are allowed");
        return;
      }

      // Validate file size (max 5MB)
      if (file.size > 5 * 1024 * 1024) {
        toast.error("File size should be less than 5MB");
        return;
      }

      setCertificateImage(file);

      // Create preview for images
      if (file.type.startsWith("image/")) {
        const reader = new FileReader();
        reader.onloadend = () => {
          setImagePreview(reader.result);
        };
        reader.readAsDataURL(file);
      } else {
        setImagePreview(null);
      }
    }
  };

  const removeImage = () => {
    setCertificateImage(null);
    setImagePreview(null);
  };

  const validateForm = () => {
    const newErrors = {};

    if (!formData.certificate_number.trim()) {
      newErrors.certificate_number = "Certificate number is required";
    }

    if (!formData.issue_date) {
      newErrors.issue_date = "Issue date is required";
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async () => {
    console.log("=== handleSubmit START ===");

    if (!validateForm()) {
      console.log("Validation failed");
      return;
    }

    setLoading(true);

    try {
      const studentId = student.id || student.student_id;
      console.log("Student ID:", studentId);
      console.log("Batch ID:", batchId);
      console.log("Certificate Number:", formData.certificate_number);
      console.log("Issue Date:", formData.issue_date);
      console.log("Has Image:", !!certificateImage);

      // ALWAYS use FormData when file upload is possible
      const formDataToSend = new FormData();
      formDataToSend.append("student_id", studentId);
      formDataToSend.append("batch_id", batchId);
      formDataToSend.append(
        "certificate_number",
        formData.certificate_number.trim(),
      );
      formDataToSend.append("issue_date", formData.issue_date);

      // Add file if exists
      if (certificateImage) {
        console.log("Adding file:", certificateImage.name);
        formDataToSend.append("certificate_image", certificateImage);
      }

      // Log FormData contents
      for (let pair of formDataToSend.entries()) {
        console.log(pair[0] + ":", pair[1]);
      }

      // Use the method that sends FormData (multipart)
      const response =
        await CertificateService.issueCertificateWithFile(formDataToSend);

      console.log("Response received:", response);

      if (response.status) {
        console.log("Certificate issued successfully!");
        toast.success(
          `Certificate issued to ${student.name || student.student_name}`,
        );

        // Call onSuccess first
        if (onSuccess) {
          console.log("Calling onSuccess callback");
          onSuccess();
        }

        // Reset form
        setFormData({
          certificate_number: "",
          issue_date: new Date().toISOString().split("T")[0],
        });
        setCertificateImage(null);
        setImagePreview(null);
        setErrors({});

        // Close modal
        console.log("Closing modal");
        onHide();
      } else {
        console.log("Response status false:", response);
        toast.error(response.message || "Failed to issue certificate");
      }
    } catch (error) {
      console.error("=== ERROR in handleSubmit ===");
      console.error("Error object:", error);
      console.error("Error response:", error.response);
      console.error("Error response data:", error.response?.data);

      const errorMsg =
        error.response?.data?.message ||
        error.message ||
        "Failed to issue certificate";
      toast.error(errorMsg);
    } finally {
      setLoading(false);
      console.log("=== handleSubmit END ===");
    }
  };
  
  const handleClose = () => {
    setFormData({
      certificate_number: "",
      issue_date: new Date().toISOString().split("T")[0],
    });
    setCertificateImage(null);
    setImagePreview(null);
    setErrors({});
    onHide();
  };

  if (!student) return null;

  return (
    <Modal show={show} onHide={handleClose} centered size="lg">
      <Modal.Header closeButton className="bg-success text-white">
        <Modal.Title>
          <FaCertificate className="me-2" />
          Issue Certificate - {student.name}
        </Modal.Title>
      </Modal.Header>

      <Modal.Body>
        <Alert variant="info" className="mb-4">
          <strong>Batch:</strong> {batchName}
        </Alert>

        <Form>
          <Form.Group className="mb-3">
            <Form.Label>Issue Date *</Form.Label>
            <div className="d-flex align-items-center">
              <div className="bg-light px-3 py-2 border rounded-start">
                <FaCalendarAlt />
              </div>
              <Form.Control
                type="date"
                name="issue_date"
                value={formData.issue_date}
                onChange={handleChange}
                isInvalid={!!errors.issue_date}
                className="rounded-start-0"
              />
            </div>
            <Form.Control.Feedback type="invalid">
              {errors.issue_date}
            </Form.Control.Feedback>
          </Form.Group>

          <Form.Group className="mb-3">
            <Form.Label>Certificate Number *</Form.Label>
            <div className="d-flex align-items-center">
              <div className="bg-light px-3 py-2 border rounded-start">
                <FaIdCard />
              </div>
              <Form.Control
                type="text"
                name="certificate_number"
                placeholder="e.g., CERT-2025-0001"
                value={formData.certificate_number}
                onChange={handleChange}
                isInvalid={!!errors.certificate_number}
                className="rounded-start-0"
              />
            </div>
            <Form.Text className="text-muted">
              Enter a unique certificate number for this student
            </Form.Text>
            <Form.Control.Feedback type="invalid">
              {errors.certificate_number}
            </Form.Control.Feedback>
          </Form.Group>

          <Form.Group className="mb-3">
            <Form.Label>Certificate Image (Optional)</Form.Label>
            <div className="border rounded p-3 text-center">
              {imagePreview ? (
                <div className="position-relative d-inline-block">
                  <img
                    src={imagePreview}
                    alt="Certificate preview"
                    style={{
                      maxWidth: "100%",
                      maxHeight: "200px",
                      borderRadius: "8px",
                    }}
                  />
                  <button
                    type="button"
                    className="position-absolute top-0 end-0 btn btn-sm btn-danger rounded-circle"
                    style={{ transform: "translate(50%, -50%)" }}
                    onClick={removeImage}
                  >
                    <FaTimes />
                  </button>
                </div>
              ) : certificateImage ? (
                <div>
                  <p className="mb-1">{certificateImage.name}</p>
                  <Button
                    variant="outline-danger"
                    size="sm"
                    onClick={removeImage}
                  >
                    Remove
                  </Button>
                </div>
              ) : (
                <div>
                  <FaUpload size={40} className="text-muted mb-2" />
                  <p className="mb-2">
                    Click or drag to upload certificate image
                  </p>
                  <Form.Control
                    type="file"
                    accept="image/jpeg,image/png,image/jpg,application/pdf"
                    onChange={handleImageChange}
                    style={{ display: "none" }}
                    id="certificate-upload"
                  />
                  <Button
                    variant="outline-primary"
                    as="label"
                    htmlFor="certificate-upload"
                  >
                    <FaUpload className="me-2" /> Choose File
                  </Button>
                  <Form.Text className="text-muted d-block mt-2">
                    Supported formats: JPG, PNG, PDF (Max 5MB)
                  </Form.Text>
                </div>
              )}
            </div>
          </Form.Group>

          <Alert variant="warning" className="mt-3">
            <strong>⚠️ Important:</strong>
            <ul className="mb-0 mt-2">
              <li>Certificate number must be unique</li>
              <li>Once issued, certificate cannot be edited</li>
              <li>Student will be able to view/download this certificate</li>
            </ul>
          </Alert>
        </Form>
      </Modal.Body>

      <Modal.Footer>
        <Button variant="secondary" onClick={handleClose} disabled={loading}>
          Cancel
        </Button>
        <Button variant="success" onClick={handleSubmit} disabled={loading}>
          {loading ? (
            <>
              <Spinner animation="border" size="sm" className="me-2" />
              Issuing...
            </>
          ) : (
            <>
              <FaCertificate className="me-2" />
              Issue Certificate
            </>
          )}
        </Button>
      </Modal.Footer>
    </Modal>
  );
};

export default IssueCertificateModal;
